import {
	CheckCircleTwoTone,
	CheckOutlined,
	CloseCircleTwoTone,
	CloseOutlined,
	ExclamationCircleOutlined,
	EyeOutlined,
} from "@ant-design/icons";
import { Button, Card, Col, Space, Modal, Row, Image, Typography, message, Tooltip } from "antd";
import Table, { TableProps } from "antd/lib/table";
import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import CustomPagination from "../../components/CustomPagination";
import NotSet from "../../components/NotSet";
import SellOrRentComponent from "../../components/SellOrRentComponent";
import { formatDate } from "../../helpers/format";
import useFetchList from "../../hooks/useFetchList";
import { EListingSellOrRent, EListingStatus, ListingProperties, ListListingQuery } from "../../types/listing.type";
import RenderListingStatus from "../../components/RenderListingStatus";
import { CustomPhotosItem } from "../../types/file.type";
import useMediaQuery from "../../hooks/useMediaQuery";
import { httpRequest } from "../../helpers/api";
import { getErrorMessage, showErrorMessage } from "../../helpers/errorHandler";
import { confirmModal } from "../../helpers/modal";

const ListingNotWatermark: React.FC = () => {
	const isBelowLg = useMediaQuery("(max-width: 991px)");

	const [showModalPhoto, setShowModalPhoto] = useState(false);
	const [tempListing, setTempListing] = useState<ListingProperties | undefined>();

	const { isLoading, data, pagination, changePage, fetchList } = useFetchList<ListingProperties, ListListingQuery>({
		endpoint: "listings/not-watermark",
	});
	const [isLoadingPhotos, setIsLoadingPhotos] = useState(false);

	const COLUMNS: TableProps<ListingProperties>["columns"] = [
		{
			title: "LISTING ID",
			key: "listingCode",
			dataIndex: "listingCode",
			width: 120,
			render: (listingCode: string, record: ListingProperties) => {
				return (
					<Link to={"/listing/" + record.listingId}>
						{listingCode ? listingCode?.toUpperCase() : <NotSet />}
					</Link>
				);
			},
		},
		{
			title: "TITLE",
			key: "title",
			dataIndex: "title",
			width: 200,
			render(value: string) {
				return value ?? <NotSet />;
			},
		},
		{
			title: "OFFICE",
			dataIndex: ["office", "officeName"],
			key: "officeName",
			width: 150,
		},
		{
			title: "AGENT NAME",
			dataIndex: ["user", "nickName"],
			key: "nickName",
			width: 200,
			render: (nickName: string) => <>{nickName ? nickName?.toUpperCase() : <NotSet />}</>,
		},
		{
			title: "PHOTO NOT WATERMARK",
			dataIndex: "photos",
			key: "photos",
			render: (photos: CustomPhotosItem[]) => {
				const hasntWatermark = photos.filter((p) => !p.hasOwnProperty("isWatermark"));

				return (
					<>
						{hasntWatermark.length} Photo{hasntWatermark.length > 1 ? "s" : ""}
					</>
				);
			},
		},
		{
			title: "POSTING DATE",
			dataIndex: "publishAt",
			key: "publishAt",
			width: 150,
			render: formatDate,
		},
		{
			title: "TYPE",
			dataIndex: "sellOrRent",
			key: "sellOrRent",
			width: 150,
			render: (sellOrRent: EListingSellOrRent) => <SellOrRentComponent value={sellOrRent} />,
		},
		{
			title: "STATUS",
			dataIndex: "status",
			key: "status",
			width: 200,
			render: (status: EListingStatus) => <RenderListingStatus status={status} />,
		},
		{
			title: "",
			dataIndex: "listingId",
			key: "listingId",
			width: 140,
			render: (listingId: string, record: ListingProperties) => {
				return (
					<Space direction="horizontal">
						<Button
							type="primary"
							icon={<EyeOutlined />}
							onClick={() => {
								setTempListing(record);
								setShowModalPhoto(true);
							}}
						/>
					</Space>
				);
			},
		},
	].filter(Boolean) as TableProps<ListingProperties>["columns"];

	const updatedPhotos = async (keys: string[], newValue: "watermarked" | "not-watermarked") => {
		setIsLoadingPhotos(true);
		try {
			const isWatermark = newValue === "watermarked" ? true : false;

			// const newArrPhotos = tempListing?.photos?.map((photo: any) =>
			// 	photo?.key === key
			// 		? {
			// 				...photo,
			// 				isWatermark: isWatermark,
			// 		  }
			// 		: photo
			// );

			const res: any = await httpRequest.post(
				"/listings/" + tempListing?.listingId + "/bulk-update-status-watermark",
				{
					keys,
					isWatermark,
					// photos: newArrPhotos,
				}
				// {
				// 	params: {
				// 		enablePortalSync: "false",
				// 	},
				// }
			);

			setTempListing(res.data.payload);

			message.success("Success change to " + (isWatermark ? "watermarked" : "Not watermarked"));
		} catch (error) {
			showErrorMessage(error, true);
		} finally {
			setIsLoadingPhotos(false);
		}
	};

	const isUnidentifiedAll = useMemo(() => {
		if (tempListing?.photos) {
			let result = true;

			for (const p of tempListing?.photos || []) {
				const photo = p as CustomPhotosItem;
				const isUnidentified =
					!photo.hasOwnProperty("isWatermark") ||
					(photo.hasOwnProperty("isWatermark") && photo.isWatermark !== false && photo.isWatermark !== true);
				if (!isUnidentified) {
					result = false;
				}
			}
			return result;
		} else {
			return false;
		}
	}, [tempListing]);

	return (
		<React.Fragment>
			<Space
				direction="vertical"
				size="large"
			>
				<Typography.Title
					ellipsis={true}
					level={3}
				>
					Data Listing Not Watermark by Genius
				</Typography.Title>
				{isBelowLg && (
					<Typography.Text style={{ color: "#8C8C8C" }}>
						Note: Best viewed on mobile in landscape mode
					</Typography.Text>
				)}
				<Card
					bordered={false}
					size="small"
				>
					<Table
						loading={isLoading}
						columns={COLUMNS}
						dataSource={data}
						rowKey="listingId"
						pagination={false}
						scroll={{ x: "100%" }}
					/>
					<CustomPagination
						current={pagination.page}
						total={pagination.totalData}
						defaultPageSize={pagination.perPage}
						pageSize={pagination.perPage}
						onChange={changePage}
					/>
				</Card>
			</Space>

			<Modal
				destroyOnClose={true}
				title="Listing Photos"
				open={showModalPhoto}
				onCancel={() => {
					setShowModalPhoto(false);
					setTempListing(undefined);
					fetchList();
				}}
				width={768}
				footer={null}
			>
				{isUnidentifiedAll && (
					<Row style={{ marginBottom: 20 }}>
						<Button
							color="danger"
							loading={isLoadingPhotos}
							type="primary"
							icon={<CloseOutlined />}
							onClick={() => {
								confirmModal({
									title: "Not watermarked?",
									subtitle: "Are you sure want to mark as not watermarked?",
									onOk: async () => {
										await updatedPhotos(
											(tempListing?.photos || []).map((p: any) => p.key),
											"not-watermarked"
										);
									},
								});
							}}
							style={{ marginRight: 10, backgroundColor: "red" }}
						>
							ALL
						</Button>

						<Button
							loading={isLoadingPhotos}
							type="primary"
							icon={<CheckOutlined />}
							onClick={() => {
								confirmModal({
									title: "Watermarked?",
									subtitle: "Are you sure want to mark as watermarked?",
									onOk: async () => {
										await updatedPhotos(
											(tempListing?.photos || []).map((p: any) => p.key),
											"watermarked"
										);
									},
								});
							}}
						>
							ALL
						</Button>
					</Row>
				)}
				<Row gutter={[24, 24]}>
					{tempListing?.photos?.map((p) => {
						const photo = p as CustomPhotosItem;
						const hasWatermarkGenius =
							photo.hasOwnProperty("isWatermark") && photo.isWatermark === true && photo.createdAt
								? true
								: false;
						const hasWatermarkManual =
							photo.hasOwnProperty("isWatermark") && photo.isWatermark === true && !photo.createdAt
								? true
								: false;
						const hasNotWatermarkManual =
							photo.hasOwnProperty("isWatermark") && photo.isWatermark === false ? true : false;
						const isUnidentified =
							!photo.hasOwnProperty("isWatermark") ||
							(photo.hasOwnProperty("isWatermark") &&
								photo.isWatermark !== false &&
								photo.isWatermark !== true);

						return (
							<Col
								span={8}
								key={photo.url}
							>
								<Space
									direction="vertical"
									style={{
										position: "relative",
									}}
								>
									<Image
										alt={photo.url}
										src={photo.url}
									/>

									<Typography.Text
										style={{
											color:
												!hasWatermarkGenius && !hasWatermarkManual
													? "darkgrey"
													: hasWatermarkGenius || hasWatermarkManual
													? "#52c41a"
													: "#ff3a3a",
											textAlign: "center",
											display: "block",
										}}
									>
										{hasWatermarkGenius || hasWatermarkManual ? (
											<CheckCircleTwoTone twoToneColor="#52c41a" />
										) : (
											<CloseCircleTwoTone twoToneColor="#ff3a3a" />
										)}{" "}
										{isUnidentified
											? "Unidentified"
											: hasWatermarkGenius || hasWatermarkManual
											? `Watermark ${hasWatermarkGenius ? "by Genius" : "(marked by admin)"}`
											: "Not Watermark"}{" "}
										{/* {!hasWatermarkGenius && !hasWatermarkManual && (
											<ExclamationCircleOutlined
												style={{
													color: "#e39909",
												}}
											/>
										)} */}
									</Typography.Text>

									{isUnidentified ? (
										<div
											style={{
												position: "absolute",
												top: -12,
												right: -12,
											}}
										>
											<Button
												color="danger"
												loading={isLoadingPhotos}
												type="primary"
												icon={<CloseOutlined />}
												onClick={() => {
													confirmModal({
														title: "Not watermarked?",
														subtitle: "Are you sure want to mark as not watermarked?",
														onOk: async () => {
															await updatedPhotos([photo.key], "not-watermarked");
														},
													});
												}}
												style={{ marginRight: 10, backgroundColor: "red" }}
											/>

											<Button
												loading={isLoadingPhotos}
												type="primary"
												icon={<CheckOutlined />}
												onClick={() => {
													confirmModal({
														title: "Watermarked?",
														subtitle: "Are you sure want to mark as watermarked?",
														onOk: async () => {
															await updatedPhotos([photo.key], "watermarked");
														},
													});
												}}
											/>
										</div>
									) : hasWatermarkManual ? (
										<div
											style={{
												position: "absolute",
												top: -12,
												right: -12,
											}}
										>
											<Button
												size="small"
												color="danger"
												loading={isLoadingPhotos}
												type="primary"
												icon={<CloseOutlined />}
												onClick={() => {
													confirmModal({
														title: "Not watermarked?",
														subtitle: "Are you sure want to mark as not watermarked?",
														onOk: async () => {
															await updatedPhotos([photo.key], "not-watermarked");
														},
													});
												}}
												style={{ backgroundColor: "red" }}
											/>
										</div>
									) : hasNotWatermarkManual ? (
										<div
											style={{
												position: "absolute",
												top: -12,
												right: -12,
											}}
										>
											<Button
												size="small"
												loading={isLoadingPhotos}
												type="primary"
												icon={<CheckOutlined />}
												onClick={() => {
													confirmModal({
														title: "Watermarked?",
														subtitle: "Are you sure want to mark as watermarked?",
														onOk: async () => {
															await updatedPhotos([photo.key], "watermarked");
														},
													});
												}}
											/>
										</div>
									) : null}
								</Space>
							</Col>
						);
					})}
				</Row>
			</Modal>
		</React.Fragment>
	);
};
export default ListingNotWatermark;
